import { Point } from 'geojson';
import { GoogleAPI, GoogleApiWrapper, Map, mapEventHandler, Marker } from 'google-maps-react';
import { useEffect, useRef } from 'react';
import { pointToLatLng as unmemoizedPointToLatLng } from '../../../utils/geolocation-format-conversion';
import { DistributiveOmit } from '../../../utils/type-utils';
import { getKey } from './location-utils';
import mapStyles from './map-styles';

type Props = {
  google: GoogleAPI;
  locations: Point[];
  /**
   * If specified, locations can only be toggled on and off from this superset and not added outside the set.
   */
  locationSuperset?: Point[];
  heightClass?: string;
  /**
   * Minimum number of locations that can be selected
   */
  min: number;
  /**
   * Maximum number of locations that can be selected
   */
  max: number;
} & (
  | {
      setLocations: (locations: Point[]) => void;
    }
  | {
      removeLocation: (location: Point) => void;
      addLocation: (location: Point) => void;
    }
);

const pointToLatLng = ((): typeof unmemoizedPointToLatLng => {
  const memoizedValues: Record<string, google.maps.LatLngLiteral> = {};
  return point => {
    const key = getKey(point);
    if (key in memoizedValues) {
      return memoizedValues[key];
    } else {
      const latLng = unmemoizedPointToLatLng(point);
      memoizedValues[key] = latLng;
      return latLng;
    }
  };
})();

const UnwrappedLocationPicker: React.FC<Props> = ({
  google,
  locations,
  locationSuperset,
  heightClass = 'h-64',
  min,
  max,
  ...locationProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const numberOfLocationsInputRef = useRef<HTMLInputElement>(null);
  const removeLocation =
    'removeLocation' in locationProps
      ? locationProps.removeLocation
      : (location: Point) => {
          const key = getKey(location);
          locationProps.setLocations(locations.filter(l => getKey(l) !== key));
        };
  const addLocation =
    'addLocation' in locationProps
      ? locationProps.addLocation
      : (location: Point) => locationProps.setLocations([...locations, location]);
  const pointsAreEqual = (point1: Point, point2: Point) => getKey(point1) === getKey(point2);
  const onReady: mapEventHandler = (props, map_) => {
    const map = map_!;
    const autocomplete = new google.maps.places.Autocomplete(inputRef.current!);
    autocomplete.bindTo('bounds', map);
    map.controls[google.maps.ControlPosition.TOP_LEFT].push(inputRef.current!);
    autocomplete.setFields(['address_components', 'geometry', 'icon', 'name']);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry == null) return;
      addLocation({ type: 'Point', coordinates: [place.geometry.location.lng(), place.geometry.location.lat()] });
      if (place.geometry.viewport) {
        map.fitBounds(place.geometry.viewport);
      } else {
        map.setCenter(place.geometry.location);
        map.setZoom(17);
      }
    });
  };
  useEffect(() => {
    const input = numberOfLocationsInputRef.current;
    if (input) {
      input.setCustomValidity('');
      input.checkValidity();
      input.addEventListener('invalid', () => {
        const value = parseInt(input.value);
        if (value < min) {
          input.setCustomValidity(`Please select at least ${min} locations.`);
        }
        if (value > max) {
          input.setCustomValidity(`You cannot select more than ${max} locations`);
        }
      });
    }
  }, [min, max, locations.length]);
  return (
    <>
      <input
        type='number'
        min={min}
        max={max}
        ref={numberOfLocationsInputRef}
        className='sr-only'
        aria-hidden='true'
        tabIndex={-1}
        value={locations.length}
        onChange={() => {}}
      />
      <input type='text' ref={inputRef} className='search-input' />
      {/* @ts-ignore React update removed children from props by default */}
      <Map
        styles={mapStyles as any}
        google={google}
        zoom={2}
        mapTypeControl={false}
        onReady={onReady}
        onClick={(_a, _b, event) => {
          const { lat, lng } = event.latLng as google.maps.LatLng;
          if (locationSuperset == null) addLocation({ type: 'Point', coordinates: [lng(), lat()] });
        }}
      >
        {locations.map(location => (
          <Marker position={pointToLatLng(location)} key={getKey(location)} onClick={() => removeLocation(location)} />
        ))}
        {locationSuperset &&
          locationSuperset
            .filter(possibleLocation => locations.every(location => !pointsAreEqual(location, possibleLocation)))
            .map(location => (
              <Marker
                position={pointToLatLng(location)}
                key={getKey(location)}
                opacity={0.5}
                onClick={() => addLocation(location)}
              />
            ))}
      </Map>
      <style jsx>{`
        /* Google Maps applies the style attribute when it is put into its position, 
        before then the input is outside the map */
        .search-input:not([style]) {
          display: none !important;
        }
        .search-input {
          @apply text-base mt-2 ml-2;
        }
        @screen sm {
          .search-input {
            @apply w-64;
          }
        }
      `}</style>
      <style jsx global>{`
        .pac-container {
          @apply bg-gray-700 font-sans border-t-2 border-gray-600 shadow;
        }
        .pac-container::after {
          display: none;
        }
        .pac-item-query {
          @apply text-gray-300 text-sm;
        }
        .pac-item {
          @apply border-t border-gray-600 text-gray-400 text-xs;
        }
        .pac-item-selected {
          @apply bg-gray-800;
        }
        .pac-item:hover {
          @apply bg-gray-600;
        }
      `}</style>
    </>
  );
};

const WrappedLocationPicker = GoogleApiWrapper({ apiKey: process.env.GOOGLE_MAPS_API_KEY, libraries: ['places'] })(
  UnwrappedLocationPicker
) as React.ComponentType<DistributiveOmit<Props, 'google'>>;

/**
 * Allows the user to pick multiple locations on a map by clicking
 */
const LocationPicker: React.FC<DistributiveOmit<Props, 'google'>> = ({ heightClass = 'h-64', ...props }) => (
  // eslint-disable-next-line react/destructuring-assignment
  <div className={`relative ${heightClass}`}>
    <WrappedLocationPicker {...props} heightClass={heightClass} />
  </div>
);
export default LocationPicker;
